import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Loader2, ArrowLeft, BookOpen, MapPin, User, Sparkles, ExternalLink, Columns2, X } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_META = {
  person:  { icon: User,   color: 'text-blue-700 dark:text-blue-300',   accent: 'border-blue-500 bg-blue-50/40 dark:bg-blue-950/20' },
  place:   { icon: MapPin, color: 'text-emerald-700 dark:text-emerald-300', accent: 'border-emerald-500 bg-emerald-50/40 dark:bg-emerald-950/20' },
  concept: { icon: BookOpen, color: 'text-amber-700 dark:text-amber-300', accent: 'border-amber-500 bg-amber-50/40 dark:bg-amber-950/20' },
};

const Factbook = () => {
  const { language } = useLanguage();
  const isES = language === 'es';
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const slug = params.get('slug') || '';

  const [query, setQuery] = useState(params.get('q') || '');
  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [entity, setEntity] = useState(null);
  const [entityLoading, setEntityLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [sibling, setSibling] = useState(null);
  const [siblingLoading, setSiblingLoading] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);

  // Live search (debounced)
  useEffect(() => {
    if (slug) return;  // viewing a single entity; skip search
    const q = query.trim();
    if (!q) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const r = await axios.get(`${BACKEND_URL}/api/factbook/search`, { params: { q } });
        setResults(r.data?.results || []);
      } catch { setResults([]); } finally { setSearchLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, slug]);

  // Entity load when slug present
  useEffect(() => {
    if (!slug) { setEntity(null); setAiSummary(null); setSibling(null); setCompareOpen(false); return; }
    setEntityLoading(true);
    setAiSummary(null);
    setSibling(null);
    setCompareOpen(false);
    axios.get(`${BACKEND_URL}/api/factbook/${encodeURIComponent(slug)}`)
      .then((r) => setEntity(r.data))
      .catch(() => setEntity({ error: true }))
      .finally(() => setEntityLoading(false));
  }, [slug]);

  const handleToggleCompare = async () => {
    if (!entity?.sibling) return;
    if (compareOpen) { setCompareOpen(false); return; }
    if (sibling) { setCompareOpen(true); return; }
    setSiblingLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/factbook/${encodeURIComponent(entity.sibling.slug)}`);
      setSibling(r.data);
      setCompareOpen(true);
    } catch {
      setSibling({ error: true });
    } finally {
      setSiblingLoading(false);
    }
  };

  const handleSelect = (s) => {
    setParams({ slug: s });
  };

  const handleBack = () => {
    setEntity(null);
    setParams(query ? { q: query } : {});
  };

  const loadAiSummary = async () => {
    if (aiSummary || aiLoading || !slug) return;
    setAiLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/factbook/${encodeURIComponent(slug)}/ai-summary`, { withCredentials: true });
      setAiSummary(r.data);
    } catch { setAiSummary({ error: true }); } finally { setAiLoading(false); }
  };

  const verseToReader = (ref) => {
    window.open(`/open?ref=${encodeURIComponent(ref)}&lens=1`, '_self');
  };

  const kindMeta = entity && KIND_META[entity.kind] ? KIND_META[entity.kind] : KIND_META.concept;
  const KindIcon = kindMeta.icon;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="factbook-page">
      <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="factbook-back">
            <ArrowLeft className="w-4 h-4 mr-1" />
            {isES ? 'Atrás' : 'Back'}
          </Button>
          <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">
            {isES ? 'Factbook — Personas, Lugares y Conceptos' : 'Factbook — People, Places & Concepts'}
          </h1>
        </div>

        {/* SEARCH */}
        {!slug && (
          <>
            <div className="flex items-center gap-2">
              <Input
                autoFocus
                placeholder={isES ? 'Busca: Moisés, Jerusalén, gracia…' : 'Search: Moses, Jerusalem, grace…'}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="factbook-search-input"
              />
              {searchLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-500" />}
            </div>

            {results.length > 0 && (
              <ScrollArea className="max-h-[70vh]">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid="factbook-results">
                  {results.map((r) => (
                    <button
                      key={r.slug}
                      onClick={() => handleSelect(r.slug)}
                      data-testid={`factbook-result-${r.slug}`}
                      className="flex items-center justify-between gap-2 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-purple-400 hover:shadow-md transition-all text-left"
                    >
                      <span className="font-semibold text-sm text-slate-800 dark:text-slate-200 truncate">{r.title}</span>
                      <ExternalLink className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    </button>
                  ))}
                </div>
              </ScrollArea>
            )}
            {query && !searchLoading && results.length === 0 && (
              <p className="text-sm text-slate-500 italic">{isES ? 'Sin resultados.' : 'No matches.'}</p>
            )}
            {!query && (
              <p className="text-sm text-slate-500">
                {isES
                  ? 'Escribe un nombre, lugar o concepto bíblico para abrir su artículo.'
                  : 'Type a biblical name, place, or concept to open its article.'}
              </p>
            )}
          </>
        )}

        {/* ENTITY VIEW */}
        {slug && entityLoading && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
          </div>
        )}
        {slug && entity && !entity.error && (
          <div className="space-y-4" data-testid="factbook-entity">
            <Button variant="outline" size="sm" onClick={handleBack} data-testid="factbook-entity-back">
              <ArrowLeft className="w-4 h-4 mr-1" />
              {isES ? 'Volver a búsqueda' : 'Back to search'}
            </Button>

            <Card className={`border-l-4 ${kindMeta.accent}`}>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className={`text-2xl flex items-center gap-2 ${kindMeta.color}`}>
                    <KindIcon className="w-5 h-5" />
                    {entity.title}
                  </CardTitle>
                  <Badge variant="outline" className={kindMeta.color}>
                    {entity.kind?.toUpperCase()}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 text-sm leading-relaxed">
                {entity.summary && (
                  <div data-testid="factbook-entity-summary">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">
                      {isES ? 'Resumen' : 'Summary'}
                    </h3>
                    <p className="whitespace-pre-wrap text-slate-700 dark:text-slate-300">{entity.summary}</p>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={loadAiSummary}
                    disabled={aiLoading || !!aiSummary}
                    variant="default"
                    size="sm"
                    data-testid="factbook-ai-summary-btn"
                    className="bg-gradient-to-r from-purple-600 to-teal-600 text-white"
                  >
                    {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    {aiSummary ? (isES ? 'Resumen IA listo' : 'AI summary loaded') : (isES ? 'Resumen IA' : 'AI Summary')}
                  </Button>
                  {entity.sibling && (
                    <Button
                      onClick={handleToggleCompare}
                      disabled={siblingLoading}
                      variant="outline"
                      size="sm"
                      data-testid="factbook-compare-btn"
                      className="border-fuchsia-500 text-fuchsia-700 dark:text-fuchsia-300 hover:bg-fuchsia-50 dark:hover:bg-fuchsia-950/30"
                    >
                      {siblingLoading
                        ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        : (compareOpen ? <X className="w-4 h-4 mr-2" /> : <Columns2 className="w-4 h-4 mr-2" />)}
                      {compareOpen
                        ? (isES ? 'Cerrar comparación' : 'Close comparison')
                        : entity.sibling.label}
                    </Button>
                  )}
                </div>

                {aiSummary && !aiSummary.error && (
                  <div data-testid="factbook-ai-summary-body" className="rounded-lg border-l-4 border-purple-500 bg-purple-50/40 dark:bg-purple-950/20 p-3">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-purple-700 dark:text-purple-300 mb-1">
                      {isES ? 'Resumen por IA' : 'AI Summary'}
                    </h3>
                    <p className="text-slate-700 dark:text-slate-300">{aiSummary.ai_summary}</p>
                  </div>
                )}

                {entity.key_verses?.length > 0 && (
                  <div data-testid="factbook-entity-verses">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">
                      {isES ? 'Versículos clave' : 'Key verses'}
                    </h3>
                    <div className="flex flex-wrap gap-1">
                      {entity.key_verses.map((v, i) => (
                        <button
                          key={i}
                          onClick={() => verseToReader(v)}
                          data-testid={`factbook-verse-${i}`}
                          className="text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-purple-100 dark:hover:bg-purple-900 transition-colors"
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {entity.see_also?.length > 0 && (
                  <div data-testid="factbook-entity-seealso">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">
                      {isES ? 'Véase también' : 'See also'}
                    </h3>
                    <div className="flex flex-wrap gap-1">
                      {entity.see_also.map((s) => (
                        <button
                          key={s.slug}
                          onClick={() => handleSelect(s.slug)}
                          data-testid={`factbook-seealso-${s.slug}`}
                          className="text-xs px-2 py-1 rounded-full border border-slate-300 dark:border-slate-700 hover:border-purple-400 transition-colors"
                        >
                          {s.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {entity.source && (
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 pt-2 border-t border-slate-200 dark:border-slate-800">
                    {entity.source}
                  </p>
                )}
              </CardContent>
            </Card>

            {compareOpen && sibling && !sibling.error && (
              <Card
                className="border-l-4 border-fuchsia-500 bg-fuchsia-50/30 dark:bg-fuchsia-950/20"
                data-testid="factbook-compare-panel"
              >
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-xl flex items-center gap-2 text-fuchsia-700 dark:text-fuchsia-300">
                      <Columns2 className="w-5 h-5" />
                      {isES ? 'Comparación: ' : 'Compare: '}{sibling.title}
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSelect(sibling.slug)}
                      data-testid="factbook-compare-open-full"
                      className="text-fuchsia-700 dark:text-fuchsia-300"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      {isES ? 'Abrir' : 'Open full entry'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 text-sm leading-relaxed">
                  {sibling.summary && (
                    <div data-testid="factbook-compare-summary">
                      <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">
                        {isES ? 'Resumen' : 'Summary'}
                      </h3>
                      <p className="whitespace-pre-wrap text-slate-700 dark:text-slate-300">{sibling.summary}</p>
                    </div>
                  )}
                  {sibling.key_verses?.length > 0 && (
                    <div data-testid="factbook-compare-verses">
                      <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">
                        {isES ? 'Versículos clave' : 'Key verses'}
                      </h3>
                      <div className="flex flex-wrap gap-1">
                        {sibling.key_verses.map((v, i) => (
                          <button
                            key={i}
                            onClick={() => verseToReader(v)}
                            data-testid={`factbook-compare-verse-${i}`}
                            className="text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-fuchsia-100 dark:hover:bg-fuchsia-900 transition-colors"
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {sibling.source && (
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 pt-2 border-t border-slate-200 dark:border-slate-800">
                      {sibling.source}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
            {compareOpen && sibling?.error && (
              <p className="text-sm text-red-600 italic" data-testid="factbook-compare-error">
                {isES ? 'No se pudo cargar la comparación' : 'Could not load comparison'}
              </p>
            )}
          </div>
        )}
        {slug && entity?.error && (
          <p className="text-sm text-red-600 italic">{isES ? 'Entrada no encontrada' : 'Entry not found'}</p>
        )}
      </div>
    </div>
  );
};

export default Factbook;
